/**
 * Cleans an email body text by:
 * - Normalizing line breaks
 * - Removing redundant consecutive duplicate URLs (especially map links and tracking pixels)
 * - Reconstituting broken table/address lines that had repetitive links injected between every word
 * - Collapsing excessive blank lines
 */
export function cleanEmailBodyText(rawText: string | null | undefined): string {
  if (!rawText) return '';

  const normalized = rawText
    .replace(/\\r\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const lines = normalized.split('\n').map((l) => l.trimEnd());
  const cleanedLines: string[] = [];
  let lastUrl: string | null = null;
  const addressWordsBuffer: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check if line is a standalone URL (e.g. <https://...> or https://...)
    const isUrl = /^<?https?:\/\/[^\s>]+>?$/i.test(line);

    if (isUrl) {
      const urlContent = line.replace(/^<|>$/g, '');
      const isMapOrTrackingUrl =
        urlContent.includes('google.com/maps/search') ||
        urlContent.includes('letsignit.com/collect') ||
        urlContent.length > 100;

      // If we have an address word buffered or duplicate map link, skip
      if (isMapOrTrackingUrl && lastUrl === urlContent) {
        continue;
      }

      if (isMapOrTrackingUrl) {
        lastUrl = urlContent;
        continue;
      }

      // Normal URL: flush address buffer and push
      if (addressWordsBuffer.length > 0) {
        cleanedLines.push(addressWordsBuffer.join(' '));
        addressWordsBuffer.length = 0;
      }
      cleanedLines.push(lines[i]);
      lastUrl = urlContent;
      continue;
    }

    // Line is text: check if it's an address fragment (short word next to map links)
    if (lastUrl && lastUrl.includes('google.com/maps/search') && line.length > 0 && line.length < 25) {
      addressWordsBuffer.push(line);
      continue;
    }

    // Flush any address buffer if found
    if (addressWordsBuffer.length > 0) {
      cleanedLines.push(addressWordsBuffer.join(' '));
      addressWordsBuffer.length = 0;
    }

    lastUrl = null;
    cleanedLines.push(lines[i]);
  }

  if (addressWordsBuffer.length > 0) {
    cleanedLines.push(addressWordsBuffer.join(' '));
  }

  // Collapse 3+ empty lines to at most 2
  const result = cleanedLines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return result;
}

/**
 * Filter attachments to exclude signature images / icons
 */
export function isSignatureAttachment(att: { filename: string; size_bytes?: number | null }): boolean {
  const filename = att.filename.toLowerCase();
  const size = att.size_bytes ?? 0;

  // image001.png, image002.png etc. with small size
  const isGenericImage = /^image\d+\.(png|jpe?g|gif|webp)$/i.test(filename);
  const isSignatureNamed = /(signature|logo|icon|social|linkedin|facebook|twitter|instagram)\.(png|jpe?g|gif|webp)$/i.test(filename);

  if ((isGenericImage || isSignatureNamed) && size < 30 * 1024) {
    return true;
  }

  // Very small image (< 10KB) without clear document name
  if (size > 0 && size < 10 * 1024 && /\.(png|jpe?g|gif)$/i.test(filename) && !filename.includes('facture') && !filename.includes('devis')) {
    return true;
  }

  return false;
}
